import { useCallback, useMemo, useState } from 'react';
import {
  UnifyLocationDetails,
  UnifyLocationNotes,
  UnifyLocationRow,
} from '../components/unify-locations-table/unify-locations-table.types';

export interface UseUnifyLocationSelectionProps {
  locations: UnifyLocationRow[];
  initialSelectedLocationIds?: string[];
  initialLocationDetailsMap?: Record<string, UnifyLocationDetails>;
  maxSelections?: number;
  minSelections?: number;
}

export interface UseUnifyLocationSelectionReturnValues {
  selectedLocationIds: string[];
  setSelectedLocationIds: React.Dispatch<React.SetStateAction<string[]>>;
  locationDetailsMap: Record<string, UnifyLocationDetails>;
  setLocationDetailsMap: React.Dispatch<
    React.SetStateAction<Record<string, UnifyLocationDetails>>
  >;
  selectedLocations: UnifyLocationRow[];
  hasMinSelections: boolean;
  areAllPocsSelected: boolean;
  canProceedToEvaluation: boolean;
  handleSelectionChange: (newSelectedIds: string[]) => void;
  handlePocChange: (locationId: string, pocEmail: string) => void;
  handleNotesChange: (locationId: string, notes: UnifyLocationNotes) => void;
}

export const useUnifyLocationSelection = ({
  locations = [],
  initialSelectedLocationIds = [],
  initialLocationDetailsMap = {},
  maxSelections = 5,
  minSelections = 1,
}: UseUnifyLocationSelectionProps): UseUnifyLocationSelectionReturnValues => {
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>(
    initialSelectedLocationIds
  );
  const [locationDetailsMap, setLocationDetailsMap] = useState<
    Record<string, UnifyLocationDetails>
  >(initialLocationDetailsMap);

  const handleSelectionChange = useCallback(
    (newSelectedIds: string[]) => {
      const cappedIds = newSelectedIds.slice(0, maxSelections);

      setSelectedLocationIds(cappedIds);
      setLocationDetailsMap((prevMap) => {
        const updatedMap: Record<string, UnifyLocationDetails> = {};
        cappedIds.forEach((id) => {
          if (prevMap[id]) {
            updatedMap[id] = prevMap[id];
          } else {
            updatedMap[id] = { pocEmail: '', notes: {} };
          }
        });
        return updatedMap;
      });
    },
    [maxSelections]
  );

  const handlePocChange = useCallback((locationId: string, pocEmail: string) => {
    setLocationDetailsMap((prev) => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        pocEmail,
      },
    }));
  }, []);

  const handleNotesChange = useCallback(
    (locationId: string, notes: UnifyLocationNotes) => {
      setLocationDetailsMap((prev) => ({
        ...prev,
        [locationId]: {
          ...prev[locationId],
          notes,
        },
      }));
    },
    []
  );

  const selectedLocations = useMemo(() => {
    const locMap = new Map(locations.map((loc) => [loc.locationId, loc]));
    return selectedLocationIds
      .map((id) => {
        return (
          locMap.get(id) || {
            locationId: id,
            locationName: id,
            slug: '',
          }
        );
      })
      .sort((a, b) => a.locationName.localeCompare(b.locationName));
  }, [locations, selectedLocationIds]);

  const hasMinSelections = selectedLocationIds.length >= minSelections;

  const areAllPocsSelected = useMemo(() => {
    if (selectedLocationIds.length === 0) return false;
    return selectedLocationIds.every((id) => {
      const details = locationDetailsMap[id];
      return Boolean(details?.pocEmail?.trim());
    });
  }, [selectedLocationIds, locationDetailsMap]);

  const canProceedToEvaluation = hasMinSelections && areAllPocsSelected;

  return {
    selectedLocationIds,
    setSelectedLocationIds,
    locationDetailsMap,
    setLocationDetailsMap,
    selectedLocations,
    hasMinSelections,
    areAllPocsSelected,
    canProceedToEvaluation,
    handleSelectionChange,
    handlePocChange,
    handleNotesChange,
  };
};
